import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import {
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
  Card,
  CardBody,
  Button,
  Chip,
  Alert,
  Select,
  Option,
} from "@material-tailwind/react";
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
  CameraIcon,
  BriefcaseIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { updateProfile, getAuth } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import heic2any from "heic2any";
import db, { storage } from "../../firebase";
import ControllerStatistics from "./ControllerStatistics";

// Available teams for report generation
const AVAILABLE_TEAMS = [
  { id: "team_a", name: "Team A" },
  { id: "team_b", name: "Team B" },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, logOut, isAdmin, LMAData } = UserAuth();
  const auth = getAuth();

  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [title, setTitle] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [thankYouBgURL, setThankYouBgURL] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingThankYouBg, setIsUploadingThankYouBg] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const thankYouBgInputRef = useRef(null);

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.uid) return;
      try {
        setIsLoading(true);
        // Get displayName and photoURL from Firebase Auth
        setDisplayName(user.displayName || "");
        setPhotoURL(user.photoURL || "");

        // Get phone number, title, and report settings from user_data collection
        const docRef = doc(db, "user_data", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPhoneNumber(data.phoneNumber || "");
          setTitle(data.title || "");
          setThankYouBgURL(data.thankYouBgURL || "");
          setSelectedTeamId(data.teamId || "");
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [user?.uid, user?.displayName, user?.photoURL]);

  const handlePhotoUpload = async (event) => {
    let file = event.target.files?.[0];
    if (!file || !user?.uid || !auth.currentUser) return;

    // Check if file is HEIC format
    const isHeic = file.type === "image/heic" ||
                   file.type === "image/heif" ||
                   file.name.toLowerCase().endsWith(".heic") ||
                   file.name.toLowerCase().endsWith(".heif");

    // Validate file type
    if (!file.type.startsWith("image/") && !isHeic) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be less than 5MB");
      return;
    }

    try {
      setIsUploadingPhoto(true);
      setError(null);

      // Convert HEIC to JPEG if needed
      if (isHeic) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9,
          });
          // heic2any can return an array, get the first item if so
          file = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        } catch (conversionError) {
          console.error("HEIC conversion error:", conversionError);
          setError(
            "Unable to convert HEIC image. On iPhone, please go to Settings > Camera > Formats and select 'Most Compatible', or share the photo to yourself via email/message to convert it to JPEG."
          );
          setIsUploadingPhoto(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }
      }

      // Upload to Firebase Storage
      const storageRef = ref(storage, `profile_photos/${user.uid}`);
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        photoURL: downloadURL,
      });

      // Also save to user_data collection for backend access
      const docRef = doc(db, "user_data", user.uid);
      await setDoc(
        docRef,
        {
          profilePhotoUrl: downloadURL,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setPhotoURL(downloadURL);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error uploading photo:", err);
      setError("Failed to upload photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleThankYouBgUpload = async (event) => {
    let file = event.target.files?.[0];
    if (!file || !user?.uid) return;

    // Check if file is HEIC format
    const isHeic = file.type === "image/heic" ||
                   file.type === "image/heif" ||
                   file.name.toLowerCase().endsWith(".heic") ||
                   file.name.toLowerCase().endsWith(".heif");

    // Validate file type
    if (!file.type.startsWith("image/") && !isHeic) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 10MB for background images)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be less than 10MB");
      return;
    }

    try {
      setIsUploadingThankYouBg(true);
      setError(null);

      // Convert HEIC to JPEG if needed
      if (isHeic) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9,
          });
          file = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        } catch (conversionError) {
          console.error("HEIC conversion error:", conversionError);
          setError(
            "Unable to convert HEIC image. Please use a JPEG or PNG file."
          );
          setIsUploadingThankYouBg(false);
          if (thankYouBgInputRef.current) {
            thankYouBgInputRef.current.value = "";
          }
          return;
        }
      }

      // Upload to Firebase Storage
      const storageRef = ref(storage, `report_assets/${user.uid}/thank_you_bg`);
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Save URL to user_data collection
      const docRef = doc(db, "user_data", user.uid);
      await setDoc(
        docRef,
        {
          thankYouBgURL: downloadURL,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setThankYouBgURL(downloadURL);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error uploading thank you background:", err);
      setError("Failed to upload image. Please try again.");
    } finally {
      setIsUploadingThankYouBg(false);
      if (thankYouBgInputRef.current) {
        thankYouBgInputRef.current.value = "";
      }
    }
  };

  const handleRemoveThankYouBg = async () => {
    if (!user?.uid) return;
    try {
      setIsUploadingThankYouBg(true);
      setError(null);

      // Remove URL from user_data collection
      const docRef = doc(db, "user_data", user.uid);
      await setDoc(
        docRef,
        {
          thankYouBgURL: "",
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setThankYouBgURL("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error removing thank you background:", err);
      setError("Failed to remove image. Please try again.");
    } finally {
      setIsUploadingThankYouBg(false);
    }
  };

  const handleTeamChange = async (teamId) => {
    if (!user?.uid) return;
    try {
      setError(null);
      setSelectedTeamId(teamId);

      // Save to user_data collection
      const docRef = doc(db, "user_data", user.uid);
      await setDoc(
        docRef,
        {
          teamId,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving team:", err);
      setError("Failed to save team selection. Please try again.");
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.uid || !auth.currentUser) return;
    try {
      setIsSaving(true);
      setError(null);
      setSaveSuccess(false);

      // Update displayName in Firebase Auth
      await updateProfile(auth.currentUser, {
        displayName: displayName,
      });

      // Update phone number and title in user_data collection
      const docRef = doc(db, "user_data", user.uid);
      await setDoc(
        docRef,
        {
          phoneNumber,
          title,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error);
    }
  };

  const getInitials = () => {
    if (displayName) {
      return displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const profileContent = (
    <div className="max-w-2xl mx-auto py-8 px-4 font-sans">
      {/* Hidden file input for photo upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        className="hidden"
      />

      {/* Profile Header */}
      <Card className="mb-6 bg-white shadow-md">
        <CardBody className="flex flex-col items-center py-8">
          <div className="relative mb-4">
            {photoURL ? (
              <img
                src={photoURL}
                alt="Profile"
                className="w-40 h-40 object-cover"
                style={{ borderRadius: '50%' }}
              />
            ) : (
              <div
                className="w-40 h-40 bg-lma-light flex items-center justify-center"
                style={{ borderRadius: '50%' }}
              >
                <span className="text-5xl font-bold text-lma-gray">
                  {getInitials()}
                </span>
              </div>
            )}
            {/* Camera overlay - appears on hover */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPhoto}
              className="absolute inset-0 w-40 h-40 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity disabled:opacity-100 cursor-pointer"
              style={{ borderRadius: '50%' }}
            >
              {isUploadingPhoto ? (
                <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <CameraIcon className="h-8 w-8 text-white" />
              )}
            </button>
          </div>
          <h2 className="text-xl font-normal tracking-widest uppercase text-lma-dark mb-1">
            {displayName || "Welcome"}
          </h2>
          <p className="text-sm text-lma-gray mb-4">
            {user?.email}
          </p>
          <div className="flex gap-2">
            <Chip
              value={isAdmin ? "ADMIN" : "USER"}
              className={isAdmin ? "bg-lma-gold text-white" : "bg-lma-gray text-white"}
              size="sm"
            />
            {LMAData && <Chip value="LMA DATA ACCESS" className="bg-lma-gold text-white" size="sm" />}
          </div>
        </CardBody>
      </Card>

      {/* Profile Form */}
      <Card className="mb-6 bg-white shadow-md">
        <CardBody>
          <h3 className="text-sm font-medium tracking-widest uppercase text-lma-dark mb-6">
            Profile Information
          </h3>

          {error && (
            <Alert color="red" className="mb-4" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {saveSuccess && (
            <Alert color="green" className="mb-4">
              Profile saved successfully!
            </Alert>
          )}

          <div className="flex flex-col gap-5">
            <div>
              <label className="block text-xs font-medium tracking-widest uppercase text-lma-gray mb-2">
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg font-sans text-sm focus:outline-none focus:border-lma-gold disabled:bg-gray-100 disabled:text-gray-500"
                />
                <UserCircleIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium tracking-widest uppercase text-lma-gray mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg font-sans text-sm focus:outline-none focus:border-lma-gold disabled:bg-gray-100 disabled:text-gray-500"
                />
                <EnvelopeIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
              <p className="text-xs text-lma-gray mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium tracking-widest uppercase text-lma-gray mb-2">
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg font-sans text-sm focus:outline-none focus:border-lma-gold disabled:bg-gray-100 disabled:text-gray-500"
                />
                <PhoneIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium tracking-widest uppercase text-lma-gray mb-2">
                Job Title
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter your job title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg font-sans text-sm focus:outline-none focus:border-lma-gold disabled:bg-gray-100 disabled:text-gray-500"
                />
                <BriefcaseIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <Button
              className="mt-4 bg-lma-gold hover:bg-lma-dark tracking-widest uppercase"
              onClick={handleSaveProfile}
              disabled={isSaving || isLoading}
              loading={isSaving}
            >
              Save Changes
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Account Info */}
      <Card className="mb-6 bg-white shadow-md">
        <CardBody>
          <h3 className="text-sm font-medium tracking-widest uppercase text-lma-dark mb-4">
            Account Details
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-lma-gold" />
              <div>
                <p className="text-xs font-medium tracking-widest uppercase text-lma-gray">
                  Role
                </p>
                <p className="text-sm text-lma-dark">
                  {isAdmin ? "Administrator" : "Standard User"}
                </p>
              </div>
            </div>
            {LMAData && (
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-lma-gold" />
                <div>
                  <p className="text-xs font-medium tracking-widest uppercase text-lma-gray">
                    LMA Data Access
                  </p>
                  <p className="text-sm text-lma-dark">
                    Enabled
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Logout */}
      <Card className="bg-white shadow-md">
        <CardBody>
          <h3 className="text-sm font-medium tracking-widest uppercase text-lma-dark mb-4">
            Session
          </h3>
          <Button
            variant="outlined"
            className="border-lma-gray text-lma-gray hover:bg-lma-gray hover:text-white tracking-widest uppercase"
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </CardBody>
      </Card>
    </div>
  );

  const reportSettingsContent = (
    <div className="max-w-2xl mx-auto py-8 px-4 font-sans">
      {/* Hidden file input for thank you background upload */}
      <input
        type="file"
        ref={thankYouBgInputRef}
        onChange={handleThankYouBgUpload}
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        className="hidden"
      />

      {/* Thank You Page Background */}
      <Card className="mb-6 bg-white shadow-md">
        <CardBody>
          <h3 className="text-sm font-medium tracking-widest uppercase text-lma-dark mb-2">
            Thank You Page Background
          </h3>
          <p className="text-xs text-lma-gray mb-6">
            Upload a background image for the Thank You / Proposal page of your market reports.
          </p>

          {error && (
            <Alert color="red" className="mb-4" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {saveSuccess && (
            <Alert color="green" className="mb-4">
              Settings saved successfully!
            </Alert>
          )}

          {thankYouBgURL ? (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={thankYouBgURL}
                  alt="Thank You Background"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  className="flex items-center gap-2 border-lma-gray text-lma-gray hover:bg-lma-gray hover:text-white tracking-widest uppercase"
                  onClick={() => thankYouBgInputRef.current?.click()}
                  disabled={isUploadingThankYouBg}
                >
                  <PhotoIcon className="h-4 w-4" />
                  Replace Image
                </Button>
                <Button
                  variant="outlined"
                  color="red"
                  className="flex items-center gap-2 tracking-widest uppercase"
                  onClick={handleRemoveThankYouBg}
                  disabled={isUploadingThankYouBg}
                >
                  <TrashIcon className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => !isUploadingThankYouBg && thankYouBgInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-lma-gold transition-colors"
            >
              {isUploadingThankYouBg ? (
                <div className="flex flex-col items-center gap-2">
                  <svg className="animate-spin h-8 w-8 text-lma-gold" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-lma-gray">Uploading...</p>
                </div>
              ) : (
                <>
                  <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-lma-dark mb-1">
                    Click to upload background image
                  </p>
                  <p className="text-xs text-lma-gray">
                    JPEG, PNG, or WebP up to 10MB
                  </p>
                </>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Team Selection */}
      <Card className="mb-6 bg-white shadow-md">
        <CardBody>
          <h3 className="text-sm font-medium tracking-widest uppercase text-lma-dark mb-2">
            Team
          </h3>
          <p className="text-xs text-lma-gray mb-6">
            Select your team for report generation.
          </p>

          <Select
            label="Select Team"
            value={selectedTeamId}
            onChange={(val) => handleTeamChange(val)}
          >
            {AVAILABLE_TEAMS.map((team) => (
              <Option key={team.id} value={team.id}>
                {team.name}
              </Option>
            ))}
          </Select>
        </CardBody>
      </Card>
    </div>
  );

  const data = [
    {
      label: "My Profile",
      value: "my-profile",
      content: profileContent,
    },
    {
      label: "Report Settings",
      value: "report-settings",
      content: reportSettingsContent,
    },
  ];

  const controllerTab = {
    label: "Controller Stats",
    value: "controller-stats",
    content: <ControllerStatistics />,
  };

  if (LMAData) {
    data.push(controllerTab);
  }

  return (
    <Tabs className="h-full flex flex-col" value="my-profile">
      <TabsHeader>
        {data.map(({ label, value }) => (
          <Tab key={value} value={value}>
            {label}
          </Tab>
        ))}
      </TabsHeader>
      <TabsBody className="flex-1 overflow-hidden">
        {data.map(({ value, content }) => (
          <TabPanel key={value} value={value} className="h-full overflow-y-auto">
            {content}
          </TabPanel>
        ))}
      </TabsBody>
    </Tabs>
  );
};

export default Profile;
